/**
 * Card de introducción al concepto de "expediente".
 *
 * Aparece arriba del header como recordatorio breve. Se cierra
 * persistentemente con localStorage (per-user) — quien ya entendió
 * el concepto no quiere verla cada vez.
 *
 * Origen: feedback founder Sprint 11 — "después de una demo, los
 * funcionarios vuelven y preguntan 'esto para qué es'. Bueno
 * mitigarlo con algo en la propia pantalla."
 */

import { useState } from "react";

import { useAuth } from "@/lib/auth";

export function IntroCard() {
  const { user } = useAuth();
  const storageKey = `kaptar.expediente.intro.dismissed.v1.${user?.id ?? "anon"}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(storageKey) === "1";
  });

  // Cuando está oculta, mostramos un trigger pequeño para reabrirla
  // en cualquier momento (Sprint 11 follow-up #1). No es invasivo —
  // solo un link discreto sobre el header. Quienes ya entendieron el
  // concepto siguen sin estorbo, pero pueden volver a verla si lo
  // necesitan.
  if (dismissed) {
    return (
      <div className="-mb-2 flex justify-end">
        <button
          type="button"
          data-testid="expediente-intro-show"
          onClick={() => {
            window.localStorage.removeItem(storageKey);
            setDismissed(false);
          }}
          className="text-xs font-medium text-slate-500 hover:text-brand-700"
        >
          ¿Qué es un expediente?
        </button>
      </div>
    );
  }

  return (
    <aside
      data-testid="expediente-intro"
      className="rounded-2xl border border-brand-100 bg-brand-50/50 p-4"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-xl">
          📋
        </span>
        <div className="flex-1">
          <p className="text-sm font-semibold text-brand-900">¿Qué es un expediente en Kaptar?</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-700">
            Es la carpeta digital donde concentras todo lo que tu municipio necesita para presentar
            el proyecto a la convocatoria — checklist de documentos requeridos, archivos adjuntos,
            código BPIN cuando se radique y el historial de cómo avanza. Lo abres aquí, lo trabajas
            y lo presentas en PIIP cuando esté listo. Kaptar te acompaña en todo el camino sin
            reemplazar al portal oficial.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            window.localStorage.setItem(storageKey, "1");
            setDismissed(true);
          }}
          className="text-xs font-medium text-slate-500 hover:text-slate-700"
          aria-label="Ocultar introducción"
        >
          Ocultar
        </button>
      </div>
    </aside>
  );
}
